import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  listInterviews,
  createInterview,
  updateInterview,
  deleteInterview,
} from "./candidateInterviewsApi";

/* --------------------------- helpers --------------------------- */
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return (parts.map((p) => p[0]).join("").slice(0, 2) || "?").toUpperCase();
}

function humanize(v) {
  const s = String(v || "").replace(/[_-]+/g, " ").trim();
  return s ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : "";
}

const STAGE_COLOR = {
  new: "info",
  applied: "info",
  screening: "warning",
  shortlisted: "warning",
  interview: "primary",
  interviewing: "primary",
  offer: "secondary",
  offered: "secondary",
  hired: "success",
  rejected: "error",
  declined: "error",
};

const money = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
};

/* --------------------------- interview feedback config --------------------------- */
const RECOMMENDATION_OPTIONS = [
  { value: "strong_yes", label: "Strong yes", color: "success", variant: "filled" },
  { value: "yes", label: "Yes", color: "success", variant: "outlined" },
  { value: "maybe", label: "Maybe", color: "warning", variant: "filled" },
  { value: "no", label: "No", color: "error", variant: "outlined" },
  { value: "strong_no", label: "Strong no", color: "error", variant: "filled" },
];

const RECOMMENDATION_BY_VALUE = RECOMMENDATION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o;
  return acc;
}, {});

const INTERVIEW_TYPE_OPTIONS = [
  { value: "onsite", label: "Onsite" },
  { value: "phone", label: "Phone" },
  { value: "video", label: "Video" },
  { value: "other", label: "Other" },
];

const STAGE_SUGGESTIONS = ["Phone Screen", "Technical", "HR", "Panel", "Final"];

const CRITERIA_FIELDS = [
  { key: "score_technical", label: "Technical" },
  { key: "score_communication", label: "Communication" },
  { key: "score_problem_solving", label: "Problem solving" },
  { key: "score_culture_fit", label: "Culture fit" },
  { key: "score_experience", label: "Experience" },
];

const EMPTY_INTERVIEW_FORM = {
  interviewer_name: "",
  interview_stage: "",
  interview_type: "",
  interview_date: "",
  overall_rating: 0,
  recommendation: "",
  score_technical: 0,
  score_communication: 0,
  score_problem_solving: 0,
  score_culture_fit: 0,
  score_experience: 0,
  strengths: "",
  concerns: "",
  comments: "",
};

// Normalize a server datetime (e.g. "2026-06-01 14:00:00" or ISO) to a yyyy-mm-dd date input value.
function toDateInputValue(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    // Fall back to leading 10 chars if it already looks like a date string.
    return String(dateStr).slice(0, 10);
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/* --------------------------- small UI pieces --------------------------- */
function SectionCard({ icon, title, subtitle, action, children, dense = false }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", bgcolor: alpha(theme.palette.background.paper, 0.96) }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${alpha(
            theme.palette.info.main,
            0.05
          )} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 34,
            height: 34,
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      <Box sx={{ p: dense ? 1.5 : 2.25 }}>{children}</Box>
    </Paper>
  );
}

function InfoRow({ icon, label, value }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: theme.palette.text.secondary, mt: 0.25, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
          {value || "-"}
        </Typography>
      </Box>
    </Stack>
  );
}

function EmptyHint({ children }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
      {children}
    </Typography>
  );
}

/* --------------------------- page --------------------------- */
export default function CandidateDetailPage() {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkBusy, setLinkBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [publicLink, setPublicLink] = useState("");
  const [expiresDays, setExpiresDays] = useState(14);
  const [links, setLinks] = useState([]);
  const [linksBusy, setLinksBusy] = useState(false);
  const [revokeBusyId, setRevokeBusyId] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // Interview feedback state
  const [interviews, setInterviews] = useState([]);
  const [interviewSummary, setInterviewSummary] = useState({
    count: 0,
    average_rating: null,
    recommendation_counts: {},
  });
  const [interviewsBusy, setInterviewsBusy] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null); // interview obj when editing, null when creating
  const [interviewForm, setInterviewForm] = useState(EMPTY_INTERVIEW_FORM);
  const [savingInterview, setSavingInterview] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // interview obj pending delete confirmation
  const [deletingInterview, setDeletingInterview] = useState(false);

  const notify = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast((p) => ({ ...p, open: false }));

  const fetchCandidate = useCallback(async () => {
    try {
      const { data } = await api.get(`/recruiter/candidates/${id}`);
      const c = data?.data || {};
      setCandidate({
        ...c,
        experiences: c.experiences ?? [],
        educations: c.educations ?? [],
        language_skills: c.language_skills ?? [],
        certificates: c.certificates ?? [],
      });
    } catch (error) {
      console.error("Error loading candidate:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLinks = useCallback(async () => {
    try {
      setLinksBusy(true);
      const res = await api.get(`/recruiter/candidates/${id}/public-cv-links`);
      setLinks(res?.data?.data?.links ?? []);
    } catch (e) {
      console.error("Error loading candidate links:", e);
    } finally {
      setLinksBusy(false);
    }
  }, [id]);

  const fetchInterviews = useCallback(async () => {
    try {
      setInterviewsBusy(true);
      const res = await listInterviews(id);
      setInterviews(Array.isArray(res?.data) ? res.data : []);
      setInterviewSummary(
        res?.summary || { count: 0, average_rating: null, recommendation_counts: {} }
      );
    } catch (e) {
      console.error("Error loading interview feedback:", e);
    } finally {
      setInterviewsBusy(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCandidate();
    fetchLinks();
    fetchInterviews();
  }, [fetchCandidate, fetchLinks, fetchInterviews]);

  /* --------------------------- interview handlers --------------------------- */
  const openCreateInterview = () => {
    setEditingInterview(null);
    setInterviewForm(EMPTY_INTERVIEW_FORM);
    setInterviewDialogOpen(true);
  };

  const openEditInterview = (iv) => {
    setEditingInterview(iv);
    setInterviewForm({
      interviewer_name: iv.interviewer_name || "",
      interview_stage: iv.interview_stage || "",
      interview_type: iv.interview_type || "",
      interview_date: toDateInputValue(iv.interview_date),
      overall_rating: Number(iv.overall_rating) || 0,
      recommendation: iv.recommendation || "",
      score_technical: Number(iv.score_technical) || 0,
      score_communication: Number(iv.score_communication) || 0,
      score_problem_solving: Number(iv.score_problem_solving) || 0,
      score_culture_fit: Number(iv.score_culture_fit) || 0,
      score_experience: Number(iv.score_experience) || 0,
      strengths: iv.strengths || "",
      concerns: iv.concerns || "",
      comments: iv.comments || "",
    });
    setInterviewDialogOpen(true);
  };

  const closeInterviewDialog = () => {
    if (savingInterview) return;
    setInterviewDialogOpen(false);
  };

  const setFormField = (key, value) => setInterviewForm((p) => ({ ...p, [key]: value }));

  const buildInterviewPayload = () => {
    const f = interviewForm;
    const numOrNull = (v) => (Number(v) >= 1 ? Number(v) : null);
    const strOrNull = (v) => {
      const s = String(v ?? "").trim();
      return s === "" ? null : s;
    };
    return {
      interviewer_name: strOrNull(f.interviewer_name),
      interview_stage: strOrNull(f.interview_stage),
      interview_type: strOrNull(f.interview_type),
      interview_date: strOrNull(f.interview_date),
      overall_rating: Number(f.overall_rating),
      recommendation: f.recommendation,
      score_technical: numOrNull(f.score_technical),
      score_communication: numOrNull(f.score_communication),
      score_problem_solving: numOrNull(f.score_problem_solving),
      score_culture_fit: numOrNull(f.score_culture_fit),
      score_experience: numOrNull(f.score_experience),
      strengths: strOrNull(f.strengths),
      concerns: strOrNull(f.concerns),
      comments: strOrNull(f.comments),
    };
  };

  const saveInterview = async () => {
    if (!(Number(interviewForm.overall_rating) >= 1)) {
      notify("error", "Please set an overall rating.");
      return;
    }
    if (!interviewForm.recommendation) {
      notify("error", "Please choose a recommendation.");
      return;
    }
    setSavingInterview(true);
    try {
      const payload = buildInterviewPayload();
      if (editingInterview) {
        await updateInterview(editingInterview.id, payload);
        notify("success", "Interview feedback updated.");
      } else {
        await createInterview(id, payload);
        notify("success", "Interview feedback added.");
      }
      setInterviewDialogOpen(false);
      await fetchInterviews();
    } catch (e) {
      notify("error", e?.response?.data?.message || e?.message || "Failed to save interview feedback.");
    } finally {
      setSavingInterview(false);
    }
  };

  const confirmDeleteInterview = async () => {
    if (!deleteTarget) return;
    setDeletingInterview(true);
    try {
      await deleteInterview(deleteTarget.id);
      notify("success", "Interview feedback deleted.");
      setDeleteTarget(null);
      await fetchInterviews();
    } catch (e) {
      notify("error", e?.response?.data?.message || e?.message || "Failed to delete interview feedback.");
    } finally {
      setDeletingInterview(false);
    }
  };

  const issuePublicLink = async () => {
    setLinkBusy(true);
    try {
      const res = await api.post(`/recruiter/candidates/${id}/public-cv-links`, {
        expires_days: Number(expiresDays) || 14,
        revoke_previous: true,
        reopen: true,
      });
      setPublicLink(res?.data?.data?.public_url || "");
      notify("success", "Public CV link created.");
      await fetchCandidate();
      await fetchLinks();
    } catch (e) {
      notify("error", e?.response?.data?.message || e?.message || "Failed to create public link.");
    } finally {
      setLinkBusy(false);
    }
  };

  const copyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      notify("success", "Link copied.");
    } catch {
      notify("error", "Failed to copy link.");
    }
  };

  const revokeLink = async (tokenId) => {
    if (!tokenId) return;
    setRevokeBusyId(tokenId);
    try {
      await api.post(`/recruiter/candidate-public-cv-links/${tokenId}/revoke`);
      notify("success", "Link revoked.");
      await fetchLinks();
    } catch (e) {
      notify("error", e?.response?.data?.message || e?.message || "Failed to revoke link.");
    } finally {
      setRevokeBusyId(null);
    }
  };

  const generateCvPdf = async () => {
    setGenBusy(true);
    try {
      const res = await api.get(`/candidates/${id}/cv.pdf`, {
        params: { download: 1 },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      notify("error", e?.response?.data?.message || e?.message || "Failed to generate CV.");
    } finally {
      setGenBusy(false);
    }
  };

  const heroBg = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(
        theme.palette.info.main,
        0.14
      )} 55%, ${alpha(theme.palette.success.main, 0.12)} 100%)`,
    [theme]
  );

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!candidate) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Alert severity="warning">No candidate found.</Alert>
      </Box>
    );
  }

  const stageColor = STAGE_COLOR[String(candidate.recruitment_stage || "").toLowerCase()] || "default";
  const cvSubmitted = Boolean(candidate.cv_submitted_at);

  const interviewCount = interviewSummary.count ?? interviews.length;
  const interviewSubtitle =
    interviewCount > 0
      ? `${interviewCount} interview${interviewCount === 1 ? "" : "s"}${
          interviewSummary.average_rating != null
            ? ` · avg ${Number(interviewSummary.average_rating).toFixed(1)}/5`
            : ""
        }`
      : "No feedback recorded";

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1180, mx: "auto" }}>
      {/* Top bar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/recruiter/candidates")}
          sx={{ fontWeight: 800, borderRadius: 2.5 }}
        >
          Candidates
        </Button>
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            startIcon={genBusy ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
            onClick={generateCvPdf}
            disabled={genBusy}
            sx={{ fontWeight: 800, borderRadius: 2.5 }}
          >
            {genBusy ? "Generating…" : "CV PDF"}
          </Button>
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => navigate(`/recruiter/candidates/${id}/edit`)}
            sx={{ fontWeight: 800, borderRadius: 2.5 }}
          >
            Edit
          </Button>
        </Stack>
      </Stack>

      {/* Hero */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 4, overflow: "hidden", mb: 2.5, bgcolor: alpha(theme.palette.background.paper, 0.9) }}
      >
        <Box sx={{ p: { xs: 2.25, md: 3 }, background: heroBg }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Avatar
              sx={{
                width: 76,
                height: 76,
                fontSize: 28,
                fontWeight: 900,
                bgcolor: alpha(theme.palette.primary.main, 0.9),
                color: theme.palette.primary.contrastText,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              {initials(candidate.full_name)}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
                {candidate.full_name || "Candidate"}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 700 }} noWrap>
                {candidate.current_job_title || "—"}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                {candidate.recruitment_stage ? (
                  <Chip
                    size="small"
                    color={stageColor}
                    label={humanize(candidate.recruitment_stage)}
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
                <Chip
                  size="small"
                  variant={cvSubmitted ? "filled" : "outlined"}
                  color={cvSubmitted ? "success" : "default"}
                  label={cvSubmitted ? `CV submitted · ${formatDate(candidate.cv_submitted_at)}` : "CV not submitted"}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<FlightTakeoffOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={candidate.relocate ? "Open to relocate" : "Not relocating"}
                  sx={{ fontWeight: 700 }}
                />
                <Chip size="small" variant="outlined" label={`#${id}`} sx={{ fontWeight: 700 }} />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Body */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr)" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        {/* MAIN */}
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {/* Interview Feedback */}
          <SectionCard
            icon={<RateReviewOutlinedIcon fontSize="small" />}
            title="Interview Feedback"
            subtitle={interviewSubtitle}
            action={
              <Button
                size="small"
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={openCreateInterview}
                sx={{ fontWeight: 800, borderRadius: 2 }}
              >
                Add feedback
              </Button>
            }
          >
            {interviewsBusy && interviews.length === 0 ? (
              <Box sx={{ py: 2, display: "grid", placeItems: "center" }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Summary strip */}
                {interviews.length > 0 ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>
                        {interviewSummary.average_rating != null
                          ? `${Number(interviewSummary.average_rating).toFixed(1)}`
                          : "—"}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                          /5
                        </Typography>
                      </Typography>
                      {interviewSummary.average_rating != null ? (
                        <Rating
                          value={Number(interviewSummary.average_rating)}
                          precision={0.1}
                          readOnly
                          size="small"
                        />
                      ) : null}
                    </Stack>
                    <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", sm: "block" } }} />
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {RECOMMENDATION_OPTIONS.map((o) => {
                        const n = Number(interviewSummary.recommendation_counts?.[o.value]) || 0;
                        if (!n) return null;
                        return (
                          <Chip
                            key={o.value}
                            size="small"
                            color={o.color}
                            variant={o.variant}
                            label={`${o.label}: ${n}`}
                            sx={{ fontWeight: 700, borderRadius: 2 }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                ) : null}

                {/* List */}
                {interviews.length === 0 ? (
                  <EmptyHint>No interview feedback yet.</EmptyHint>
                ) : (
                  <Stack spacing={1.5}>
                    {interviews.map((iv, i) => {
                      const rec = RECOMMENDATION_BY_VALUE[iv.recommendation];
                      const stageType = [humanize(iv.interview_stage), humanize(iv.interview_type)]
                        .filter(Boolean)
                        .join(" · ");
                      const criteria = CRITERIA_FIELDS.filter((c) => Number(iv[c.key]) >= 1);
                      return (
                        <Box
                          key={iv.id ?? i}
                          sx={{
                            p: 1.75,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                {iv.interviewer_name || "Interviewer"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[stageType, formatDate(iv.interview_date)].filter((s) => s && s !== "-").join(" · ") ||
                                  "—"}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEditInterview(iv)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => setDeleteTarget(iv)}>
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1 }}
                          >
                            {Number(iv.overall_rating) >= 1 ? (
                              <Rating value={Number(iv.overall_rating)} max={5} readOnly size="small" />
                            ) : null}
                            {rec ? (
                              <Chip
                                size="small"
                                color={rec.color}
                                variant={rec.variant}
                                label={rec.label}
                                sx={{ fontWeight: 700, borderRadius: 2 }}
                              />
                            ) : null}
                          </Stack>

                          {criteria.length > 0 ? (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                              {criteria.map((c) => (
                                <Chip
                                  key={c.key}
                                  size="small"
                                  variant="outlined"
                                  label={`${c.label} ${iv[c.key]}/5`}
                                  sx={{ height: 22, fontWeight: 600, borderRadius: 1.5 }}
                                />
                              ))}
                            </Stack>
                          ) : null}

                          {iv.strengths || iv.concerns || iv.comments ? (
                            <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                              {iv.strengths ? (
                                <Box>
                                  <Typography variant="caption" color="success.main" sx={{ fontWeight: 800 }}>
                                    Strengths
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                    {iv.strengths}
                                  </Typography>
                                </Box>
                              ) : null}
                              {iv.concerns ? (
                                <Box>
                                  <Typography variant="caption" color="error.main" sx={{ fontWeight: 800 }}>
                                    Concerns
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                    {iv.concerns}
                                  </Typography>
                                </Box>
                              ) : null}
                              {iv.comments ? (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                    Comments
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                                    {iv.comments}
                                  </Typography>
                                </Box>
                              ) : null}
                            </Stack>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            )}
          </SectionCard>

          {candidate.notes ? (
            <SectionCard icon={<NotesRoundedIcon fontSize="small" />} title="Notes">
              <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {candidate.notes}
              </Typography>
            </SectionCard>
          ) : null}

          <SectionCard
            icon={<WorkOutlineRoundedIcon fontSize="small" />}
            title="Experience"
            subtitle={`${candidate.experiences.length} entr${candidate.experiences.length === 1 ? "y" : "ies"}`}
          >
            {candidate.experiences.length === 0 ? (
              <EmptyHint>No experience recorded.</EmptyHint>
            ) : (
              <Stack spacing={2}>
                {candidate.experiences.map((x, i) => (
                  <Box key={x.id ?? i}>
                    {i > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {x.position || "Role"}
                      {x.company ? (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {"  @ "}
                          {x.company}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(x.start_date)} – {x.end_date ? formatDate(x.end_date) : "Present"}
                    </Typography>
                    {x.description ? (
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                        {x.description}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            icon={<SchoolOutlinedIcon fontSize="small" />}
            title="Education"
            subtitle={`${candidate.educations.length} entr${candidate.educations.length === 1 ? "y" : "ies"}`}
          >
            {candidate.educations.length === 0 ? (
              <EmptyHint>No education recorded.</EmptyHint>
            ) : (
              <Stack spacing={2}>
                {candidate.educations.map((e, i) => (
                  <Box key={e.id ?? i}>
                    {i > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {e.degree || "Degree"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {e.institution || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[e.start_year, e.end_year].filter(Boolean).join(" – ") || "-"}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            icon={<TranslateRoundedIcon fontSize="small" />}
            title="Languages"
            subtitle={`${candidate.language_skills.length} listed`}
          >
            {candidate.language_skills.length === 0 ? (
              <EmptyHint>No languages listed.</EmptyHint>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {candidate.language_skills.map((l, i) => (
                  <Chip
                    key={l.id ?? i}
                    size="small"
                    variant="outlined"
                    label={l.proficiency ? `${l.language || "-"} — ${humanize(l.proficiency)}` : l.language || "-"}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            icon={<WorkspacePremiumOutlinedIcon fontSize="small" />}
            title="Certificates"
            subtitle={`${candidate.certificates.length} file(s)`}
          >
            {candidate.certificates.length === 0 ? (
              <EmptyHint>No certificates uploaded.</EmptyHint>
            ) : (
              <Stack spacing={1}>
                {candidate.certificates.map((c, i) => (
                  <Stack
                    key={c.id ?? i}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ p: 1, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <WorkspacePremiumOutlinedIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {c.title || `Certificate ${i + 1}`}
                      </Typography>
                    </Stack>
                    {c.file_url ? (
                      <Button
                        size="small"
                        endIcon={<LaunchRoundedIcon />}
                        href={c.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open
                      </Button>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Stack>

        {/* SIDEBAR */}
        <Stack spacing={2.5} sx={{ minWidth: 0, position: { md: "sticky" }, top: { md: 16 } }}>
          <SectionCard icon={<BadgeOutlinedIcon fontSize="small" />} title="Contact">
            <Stack spacing={1.75}>
              <InfoRow icon={<EmailOutlinedIcon fontSize="small" />} label="Email" value={candidate.email} />
              <InfoRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Phone" value={candidate.phone} />
              <InfoRow icon={<HomeOutlinedIcon fontSize="small" />} label="Address" value={candidate.address} />
            </Stack>
          </SectionCard>

          <SectionCard icon={<PaymentsOutlinedIcon fontSize="small" />} title="Compensation & availability">
            <Stack spacing={1.75}>
              <InfoRow
                icon={<PaymentsOutlinedIcon fontSize="small" />}
                label="Expected salary"
                value={money(candidate.expected_salary)}
              />
              <InfoRow
                icon={<PaymentsOutlinedIcon fontSize="small" />}
                label="Current salary"
                value={money(candidate.current_salary)}
              />
              <InfoRow
                icon={<EventAvailableOutlinedIcon fontSize="small" />}
                label="Joining availability"
                value={formatDate(candidate.joining_availability_date)}
              />
            </Stack>
          </SectionCard>

          <SectionCard
            icon={<LinkRoundedIcon fontSize="small" />}
            title="Public CV link"
            subtitle="Share a time-limited link to this CV"
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <TextField
                  type="number"
                  label="Expires (days)"
                  value={expiresDays}
                  onChange={(e) => setExpiresDays(e.target.value)}
                  inputProps={{ min: 1, max: 365 }}
                  size="small"
                  sx={{ width: 130 }}
                />
                <Button
                  variant="contained"
                  startIcon={linkBusy ? <CircularProgress size={16} color="inherit" /> : <LinkRoundedIcon />}
                  onClick={issuePublicLink}
                  disabled={linkBusy}
                  sx={{ fontWeight: 800, borderRadius: 2, flex: 1 }}
                >
                  {linkBusy ? "Creating…" : "Create"}
                </Button>
              </Stack>

              {publicLink ? (
                <Stack spacing={1}>
                  <TextField value={publicLink} fullWidth size="small" InputProps={{ readOnly: true }} label="Link" />
                  <Stack direction="row" spacing={1}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyLink}>
                      Copy
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<LaunchRoundedIcon />}
                      onClick={() => window.open(publicLink, "_blank", "noopener,noreferrer")}
                    >
                      Open
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              <Divider sx={{ my: 0.5 }} />

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                LINK HISTORY
              </Typography>

              {linksBusy ? (
                <Box sx={{ py: 1, display: "grid", placeItems: "center" }}>
                  <CircularProgress size={18} />
                </Box>
              ) : links.length === 0 ? (
                <EmptyHint>No public links created yet.</EmptyHint>
              ) : (
                <Stack spacing={1}>
                  {links.map((l) => {
                    const active = String(l.status || "active").toLowerCase() === "active";
                    return (
                      <Box
                        key={l.id}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          bgcolor: active ? alpha(theme.palette.success.main, 0.05) : "transparent",
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              #{l.id}
                            </Typography>
                            <Chip
                              size="small"
                              color={active ? "success" : "default"}
                              variant={active ? "filled" : "outlined"}
                              label={String(l.status || "active").toUpperCase()}
                              sx={{ height: 20, fontWeight: 800 }}
                            />
                          </Stack>
                          {active ? (
                            <Tooltip title="Revoke link">
                              <span>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => revokeLink(l.id)}
                                  disabled={revokeBusyId === l.id}
                                >
                                  {revokeBusyId === l.id ? "…" : "Revoke"}
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                          Expires {formatDate(l.expires_at)} · Last used {formatDate(l.last_used_at)} · Created{" "}
                          {formatDate(l.created_at)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>

      {/* Add / Edit interview dialog */}
      <Dialog
        open={interviewDialogOpen}
        onClose={closeInterviewDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingInterview ? "Edit interview feedback" : "Add interview feedback"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Interviewer name"
                value={interviewForm.interviewer_name}
                onChange={(e) => setFormField("interviewer_name", e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Stage"
                value={interviewForm.interview_stage}
                onChange={(e) => setFormField("interview_stage", e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g. Technical"
                helperText={STAGE_SUGGESTIONS.join(" · ")}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Type"
                select
                value={interviewForm.interview_type}
                onChange={(e) => setFormField("interview_type", e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {INTERVIEW_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Interview date"
                type="date"
                value={interviewForm.interview_date}
                onChange={(e) => setFormField("interview_date", e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Overall rating *
                </Typography>
                <Box>
                  <Rating
                    value={Number(interviewForm.overall_rating) || 0}
                    max={5}
                    onChange={(_e, v) => setFormField("overall_rating", v || 0)}
                  />
                </Box>
              </Box>
              <TextField
                label="Recommendation *"
                select
                required
                value={interviewForm.recommendation}
                onChange={(e) => setFormField("recommendation", e.target.value)}
                fullWidth
                size="small"
              >
                {RECOMMENDATION_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                CRITERIA (1–5)
              </Typography>
            </Divider>

            <Stack spacing={1}>
              {CRITERIA_FIELDS.map((c) => (
                <Stack
                  key={c.key}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {c.label}
                  </Typography>
                  <Rating
                    value={Number(interviewForm[c.key]) || 0}
                    max={5}
                    size="small"
                    onChange={(_e, v) => setFormField(c.key, v || 0)}
                  />
                </Stack>
              ))}
            </Stack>

            <Divider />

            <TextField
              label="Strengths"
              value={interviewForm.strengths}
              onChange={(e) => setFormField("strengths", e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Concerns"
              value={interviewForm.concerns}
              onChange={(e) => setFormField("concerns", e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Comments"
              value={interviewForm.comments}
              onChange={(e) => setFormField("comments", e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeInterviewDialog} disabled={savingInterview} sx={{ fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveInterview}
            disabled={savingInterview}
            startIcon={savingInterview ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          >
            {savingInterview ? "Saving…" : editingInterview ? "Save changes" : "Add feedback"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deletingInterview ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete interview feedback?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove the scorecard
            {deleteTarget?.interviewer_name ? ` from ${deleteTarget.interviewer_name}` : ""}. This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deletingInterview} sx={{ fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteInterview}
            disabled={deletingInterview}
            startIcon={deletingInterview ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          >
            {deletingInterview ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
